import type { ModuleCatalog } from "./ConfiguratorModule.js";
import { seedMaterialPartGroupsCache } from "../../loader/MaterialInfoCache.js";

export type ConfiguratorVariantOption = {
  name: string;
  type: string;
  options: string[] | ConfiguratorVariantOption[];
  selected?: string | ConfiguratorVariantOption;
  url?: string;
  invisible?: boolean | null;
};

/** fetchMaterialOptions API'sinden gelen kumaş/renk görsel seçenekleri */
export type MaterialOption = {
  name: string;
  url: string;
};

export type ConfiguratorVariant = {
  variantId?: string;
  /** Ürün / takım adı (ör. Lyno & Roa 97x160 Cm Masa Takımı). */
  name: string;
  /** Seçili variant etiketi (ör. Lyno 97x160 Cm Sabit … Kadife). */
  label?: string;
  explanation: string;
  urlHandle?: string;
  productId?: string;
  /** armType, legType, legColor, fabricType, fabricColor, backCushion, blockType, size vb. */
  attributes: Record<string, string | undefined>;
};

export const getVariantAttr = (
  v: ConfiguratorVariant,
  key: string
): string | undefined => v.attributes?.[key];

export const setVariantAttr = (
  v: ConfiguratorVariant,
  key: string,
  value?: string
): void => {
  if (!v.attributes) v.attributes = {};
  v.attributes[key] = value;
};

export type ConfiguratorVariantModule = ConfiguratorVariantOption & {
  variant: ConfiguratorVariant;
  /** API'den null gelir; optionDetails _optionDetails Map'ine materialOptions'tan doldurulur */
  optionDetails: ConfiguratorVariantOption[] | null;
};

/** variant/info cevabındaki değer + görsel metadata (configurator_option_value). */
export type ConfiguratorOptionValueMeta = {
  value: string;
  label?: string | null;
  iconUrl?: string | null;
  /** Temsilci material adı (örn. "Kadife-Ekru"); fabricType değişiminde renk türetmek için. */
  materialName?: string | null;
  color?: string | null;
  shape?: string | null;
  /** Menü içi görüntüleme sırası; null ise sona düşer. */
  displayOrder?: number | null;
  /** Işık/HDR preseti (JSON, bkz. LightPreset); null ise varsayılan ışık. */
  lightPreset?: string | null;
};

/** variant/info cevabındaki option menüsü (yeni sözleşme). */
export type ConfiguratorResolvedOption = {
  type: string;
  label?: string | null;
  displayOrder?: number | null;
  cameraPreset?: string | null;
  invisible?: boolean | null;
  matchPriority?: number | null;
  values: ConfiguratorOptionValueMeta[];
  selected?: string | null;
};

/**
 * Tek kapı `POST /api/configurator2/variant/info` cevabı.
 * Mod A (variantId): optionDetails dolu. Mod B (configuratorId + attributes): optionDetails null.
 */
export type ConfiguratorVariantResolve = {
  configuratorId?: number | null;
  variant?: ConfiguratorVariant | null;
  options: ConfiguratorResolvedOption[] | null;
  optionDetails?: MaterialOption[] | null;
  exactMatch?: boolean | null;
  matchScore?: number | null;
  requestedAttributeCount?: number | null;
  mismatchedAttributes?: string[] | null;
  warnings?: string[] | null;
};

export type CONFIGURATOR_MODEL_TYPE = {
  name: string;
  configurator: CONFIGURATOR_TYPE;
  models: MODEL_TYPE[];
  materialPartGroups: {
    title: string;
    code: string;
    materialGroups: { name: string; order: number; materials: any[] }[];
  }[];
  /** `api/configurator/zip/{id}` yanıtından gelen modül kataloğu. */
};

export type CONFIGURATOR_TYPE = {
  excludeModels?: number[];
  backs?: MODEL_PART_TYPE[];
  legs?: MODEL_PART_TYPE[];
  sides?: MODEL_PART_TYPE[];
  events?: EVENT_TYPE[];
  /** İç genişlik (cm) → yüklenecek model id listesi */
  sizes?: Record<number, number[]>;
  /** Varyant body key → legacy model id listesi veya v2 layout grafı */
  body?: Record<string, BodyLayoutEntry>;
  /**
   * Body map lookup şablonu. `${name}` variant adı, `${attr}` / `${attr:varsayılan}` attribute.
   * Yoksa eski headType+size / table-set / size / body attr sırası kullanılır.
   */
  bodyExpression?: string;
  /**
   * `materialPartGroups[].code` — kumaş/renk eşlemesi bu grupta aranır.
   * Yoksa başlık anahtar kelimesi veya son çare `"1"`.
   */
  fabricMaterialGroupCode?: string;
  /**
   * `materialPartGroups[].code` — ayak rengi bu grupta aranır.
   * Yoksa başlık anahtar kelimesi veya son çare `"2"`.
   */
  legMaterialGroupCode?: string;

  moduleCatalog?: ModuleCatalog;

};

/**
 * Body layout v2 — ayrı node listesi + joint edge listesi.
 * `out` = child model joint outType, `in` = parent model joint inType.
 */
export type BodyLayoutJoint = {
  /** Child node index (0-based, nodes dizisindeki sıra) */
  child: number;
  /** Parent node index (0-based) */
  parent: number;
  out: string;
  in: string;
};

export type BodyLayoutV2 = {
  v: 2;
  /** Yükleme sırası; index 0 = kök */
  nodes: number[];
  joints: BodyLayoutJoint[];
  /** cm: "201x238" / [201,238] = en+derinlik; üçüncü bileşen = yükseklik */
  size?: BodyLayoutSizeSpec;
};

/** en×derinlik veya en×derinlik×yükseklik (cm) */
export type BodyLayoutSizeSpec = string | number[];

/** Eski gömülü join formatı (normalize edilir) */
export type BodyLayoutNodeLegacy = {
  id: number;
  join?: { p: number; out: string; in: string };
};

/** Legacy: `[60044, 90501, ...]` veya v2 graf. */
export type BodyLayoutEntry = number[] | BodyLayoutV2;

export function isBodyLayoutV2(entry: BodyLayoutEntry): entry is BodyLayoutV2 {
  return (
    !Array.isArray(entry) &&
    typeof entry === "object" &&
    entry !== null &&
    entry.v === 2 &&
    Array.isArray(entry.nodes)
  );
}

/** Gömülü join'li eski v2 JSON → nodes + joints */
export function normalizeBodyLayoutV2(
  entry: BodyLayoutV2 | { v: 2; nodes: BodyLayoutNodeLegacy[] },
): BodyLayoutV2 {
  const first = entry.nodes[0];
  if (typeof first === "number") {
    return entry as BodyLayoutV2;
  }
  const legacyNodes = entry.nodes as BodyLayoutNodeLegacy[];
  const nodes = legacyNodes.map((n) => n.id);
  const joints: BodyLayoutJoint[] = [];
  legacyNodes.forEach((node, index) => {
    if (index === 0 || !node.join) {
      return;
    }
    joints.push({
      child: index,
      parent: node.join.p,
      out: node.join.out,
      in: node.join.in,
    });
  });
  return { v: 2, nodes, joints };
}

export function isBodyLayoutLegacy(entry: BodyLayoutEntry): entry is number[] {
  return Array.isArray(entry);
}

export type MODEL_PART_TYPE = {
  name: string;
  type: string;
  imageURL: string;
  joints: JOINT_TYPE[];
  match: number | number[];
};

export type JOINT_TYPE = {
  name: string;
  type: string;
  match: number | Record<string, number>;
  id?: number;
};

export type EVENT_TYPE = {
  when: { type: string; items: number[] };
  operations: { action: string; parameters?: Record<string, any> }[];
};

export type MODEL_TYPE = {
  id: number;
  name: string;
  gltf: string;
  thumbnailFileUrl: string;
  replaceType: number;
  moveType: number;
  parts: any[];
  partMaterialGroups: any[];
  jointInfo: any;
  price?: number | null;
  x: number | null;
  y: number | null;
  z: number | null;
  centerX?: number | null;
  centerY?: number | null;
  centerZ?: number | null;
  orderIndex: number;
};

function createDefaultData(): CONFIGURATOR_MODEL_TYPE {
  return {
    name: "Default",
    configurator: {
      excludeModels: [],
      backs: [],
      sides: [],
      legs: [],
      events: [],
      sizes: {},
      body: {},
    },
    models: [],
    materialPartGroups: [],
  };
}

const _store = new Map<string, CONFIGURATOR_MODEL_TYPE>();

export function getConfiguratorData(id: string): CONFIGURATOR_MODEL_TYPE {
  let data = _store.get(id);
  if (!data) {
    data = createDefaultData();
    _store.set(id, data);
  }
  return data;
}

export function setConfiguratorData(
  id: string,
  incoming: Partial<CONFIGURATOR_MODEL_TYPE>,
): CONFIGURATOR_MODEL_TYPE {
  const data = getConfiguratorData(id);
  if (incoming.name !== undefined) data.name = incoming.name;
  if (incoming.configurator !== undefined) data.configurator = incoming.configurator;
  if (incoming.models !== undefined) data.models = incoming.models;
  if (incoming.materialPartGroups !== undefined) {
    data.materialPartGroups = incoming.materialPartGroups;
    seedMaterialPartGroupsCache(incoming.materialPartGroups);
  }
  //if (incoming.moduleCatalog !== undefined) data.moduleCatalog = incoming.moduleCatalog;
  return data;
}

export function removeConfiguratorData(id: string): boolean {
  return _store.delete(id);
}

/**
 * @deprecated Geriye dönük uyumluluk için. Yeni kodda getConfiguratorData(id) kullanın.
 */
export const CONFIGURATOR_DATA: CONFIGURATOR_MODEL_TYPE = getConfiguratorData("default");

export * from "./ConfiguratorModule.js";
